import * as React from "react";
import { withRouter, RouteComponentProps } from "react-router-dom";
import _ from "lodash";
import moment from "moment";
import { withFormik, FormikProps, FormikErrors } from "formik";
import { inputProps } from "utilities/formik";
import Text from "components/LegacyText";
import FormSection from "components/Form/Section";
import FormTextBox from "components/Form/TextBox";
import ActionFooter from "components/ActionFooter";
import ImageGroup from "components/ImageGroup";
import FilePicker, { IFile } from "components/Form/FilePicker";
import SearchableMultiSelect from "../../../components/LegacySearchable/SearchableMultiSelect";
import styles from "./styles.scss";
import DatePicker from "components/DatePicker";
import { isPinata } from "utilities/flavor";

type Asset = IFile & { isPrimary?: boolean };
type ID = string;

export interface Profile {
  zip?: string | null;
  selfSummary?: string | null;
  shirtSize?: string | null;
  birthday?: moment.Moment | null;
  languages?: Array<{ key: string; label: string }>;
}

export interface ProfileMe {
  id: string;
  firstName: string;
  lastName: string;
  assets: Asset[];
  documents: IFile[];
  profile: Profile | null;
}

type FormValues = Partial<{
  firstName: string;
  lastName: string;
  assets: Asset[];
  documents: IFile[];
  profile: Profile;
  selfSummary: string;
  shirtSize: string;
}>;

interface FormProps {
  onSubmit: (v: FormValues) => Promise<any>;
  me: ProfileMe;
  languages: Array<{ key: string; label: string }>;
  success?: boolean;
  error?: boolean;
}

type Props = FormProps & FormikProps<FormValues> & RouteComponentProps;

interface State {
  submitted: boolean;
  primaryImage?: Asset | null;
  secondaryImages: Asset[];
  languages: ID[] | null;
}

class Form extends React.Component<Props, State> {
  readonly state: State = {
    submitted: false,
    primaryImage: this.props.values.assets ? _.find(this.props.values.assets, { isPrimary: true }) : null,
    secondaryImages: this.props.values.assets ? this.props.values.assets.filter(a => !a.isPrimary) : [],
    languages: null,
  };

  private handleSubmit = (e?: React.FormEvent<HTMLFormElement>) => {
    this.setState({ submitted: true });
    this.props.handleSubmit(e);
  };

  private setPrimaryImageValue = (value: Asset[]) => {
    const primaryImage = value.length ? { url: value[0].url, isPrimary: true } : null;
    this.setState({ primaryImage });
    this.props.setFieldValue(
      "assets",
      primaryImage ? this.state.secondaryImages.concat(primaryImage) : this.state.secondaryImages,
    );
  };

  private setImageValue = (values: Asset[]) => {
    const secondaryImages = values;
    this.setState({ secondaryImages });
    if (this.state.primaryImage) this.props.setFieldValue("assets", secondaryImages.concat(this.state.primaryImage));
  };

  private setDocumentsValues = (values: IFile[]) => {
    const vals = values.map(({ url, caption }) => ({ url, name: caption }));
    this.props.setFieldValue("documents", vals);
  };

  private handleLanguagesChanged = (languages: ID[] | null) => {
    this.props.setFieldValue(
      "profile.languages",
      languages && languages.length > 0 ? languages.map(l => ({ key: l })) : null,
    );
  };

  public render() {
    const {
      errors,
      success,
      error,
      values: { profile, documents = [] },
      setFieldValue,
      languages,
      history,
      location,
    } = this.props;
    const { submitted, primaryImage, secondaryImages } = this.state;
    const curriedSetFieldValue = _.curry(setFieldValue, 2);
    const input = inputProps(this.props);

    return (
      <div>
        <form className={styles.form}>
          <Text.Display className={styles.header}>Profile</Text.Display>
          {success && (
            <div className={styles.success}>
              <Text.H1>Information Updated!</Text.H1>
            </div>
          )}

          {error && (
            <Text.ErrorMessage className={styles.networkError}>
              Oops, something went wrong. Please try again.
            </Text.ErrorMessage>
          )}

          <FormSection>
            <FormTextBox
              {...input("firstName")}
              errorMessage={submitted && errors.firstName}
              placeholder="ex. Jane"
              label="First Name"
              testId={"gogetter.profile.firstName"}
            />
            <FormTextBox
              {...input("lastName")}
              errorMessage={submitted && errors.lastName}
              placeholder="ex. Doe"
              label="Last Name"
              testId={"gogetter.profile.lastName"}
            />
            {isPinata && (
              <>
                <FormTextBox
                  {...input("profile.selfSummary")}
                  label="Self Summary"
                  multiline
                  errorMessage={submitted && errors.selfSummary}
                  placeholder="ex. I've worked for many different brands, and enjoy field marketing"
                  optionalLabel
                  testId={"gogetter.profile.selfSummary"}
                />

                <FormTextBox
                  {...input("profile.shirtSize")}
                  label="Shirt Size"
                  errorMessage={submitted && errors.shirtSize}
                  placeholder="ex. M"
                  optionalLabel
                  testId={"gogetter.profile.shirtSize"}
                />
              </>
            )}
            <DatePicker
              id="birthdayDatePicker"
              direction={"up"}
              placeholder={"mm/dd/yyyy"}
              value={(profile && profile.birthday) || null}
              onChange={curriedSetFieldValue("profile.birthday")}
              isOutsideRange={() => false}
              label={"Birthday"}
              optionalLabel
              testId={"gogetter.profile.apartment"}
            />

            {isPinata && (
              <SearchableMultiSelect
                name={"Languages"}
                placeholder={"Select languages you speak"}
                selectedItems={((profile && profile.languages) || []).map(({ key }) => key)}
                onChange={this.handleLanguagesChanged}
                items={languages.map(({ key, label }) => ({ id: key, name: label }))}
                testId={"gogetter.profile.languages"}
              />
            )}
          </FormSection>

          <ImageGroup>
            <div className={styles.imageSection}>
              <Text.Label2 className={styles.imageTitle}>Primary profile pic</Text.Label2>
              <FilePicker
                size={77}
                value={primaryImage ? [{ url: primaryImage.url }] : []}
                onChange={this.setPrimaryImageValue}
                accepts={"image/*, image/heic"}
                testId={"gogetter.profile.primaryImage"}
              />
            </div>
            <div className={styles.imageSection}>
              <Text.Label2 className={styles.imageTitle}>Secondary profile pics</Text.Label2>
              <FilePicker
                size={77}
                value={secondaryImages || []}
                onChange={this.setImageValue}
                accepts={"image/*, image/heic"}
                testId={"gogetter.profile.secondaryImage"}
              />
            </div>
          </ImageGroup>

          <div className={styles.documentsSection}>
            <hr />
            <Text.Label2 className={styles.imageTitle}>Attached documents</Text.Label2>
            <FilePicker
              size={77}
              value={documents.map(({ url, name }) => ({ url, caption: name })) || []}
              onChange={this.setDocumentsValues}
              caption
              accepts={".pdf,.doc,.txt,.docx,.xls,.xlsx"}
              testId={"gogetter.profile.attachedDocument"}
              secure
            />
          </div>

          {!!Object.keys(errors).length && (
            <Text.ErrorMessage>
              Please make sure that all of the required fields above are filled out!
            </Text.ErrorMessage>
          )}
        </form>

        <ActionFooter
          title={`UPDATE`}
          onAction={() => this.handleSubmit()}
          className={styles.formBtn}
          testId={"gogetter.profile.updateProfile"}
          onCancel={() => {
            history.push({
              ...location,
              pathname: "/settings",
            });
          }}
        />
      </div>
    );
  }
}

const Validated = withFormik<Props, FormValues>({
  mapPropsToValues({ me }: FormProps) {
    return me
      ? {
          ...me,
          profile: me.profile
            ? {
                ...me.profile,
                birthday: me.profile.birthday
                  ? moment.isMoment(me.profile.birthday)
                    ? me.profile.birthday
                    : moment(me.profile.birthday)
                  : undefined,
              }
            : {},
        }
      : {};
  },
  validateOnChange: true,
  validate(values) {
    const { firstName, lastName } = values;
    const errors: FormikErrors<FormValues> = {};

    if (!firstName) errors.firstName = "You need to fill this out";
    if (!lastName) errors.lastName = "You need to fill this out";

    return errors;
  },
  handleSubmit(values, { props }: { props: FormProps }) {
    const {
      onSubmit,
      me: { documents },
    } = props;

    const updatedDocs = (values.documents || []).map(d => {
      const foundInProps = documents.find(dc => dc.url === d.url);
      return { url: d.url, name: d.name, id: foundInProps ? foundInProps.id : undefined };
    });

    onSubmit({
      ...values,
      assets: (values.assets || []).map(({ url, isPrimary }) => ({ url, isPrimary })),
      documents: updatedDocs,
    });
  },
})(Form);

export default withRouter((props: any) => <Validated {...props} />);
